import type { CoachingPlan, CommitmentDuration, CommitmentOption } from '../types/index.ts';

/**
 * Commitment durations and discounts.
 * Centralized configuration.
 */
export const COMMITMENT_OPTIONS: CommitmentOption[] = [
  { months: 1, discountPercentage: 0, label: '1 Month' },
  { months: 3, discountPercentage: 10, label: '3 Months' },
  { months: 6, discountPercentage: 15, label: '6 Months' },
  { months: 12, discountPercentage: 20, label: '12 Months' }
];

export interface PricingCalculation {
  baseMonthlyRate: number;
  discountPercentage: number;
  discountedMonthlyRate: number;
  discountedMonthlyRateFormatted: string;
  totalCommitmentValue: number;
  totalCommitmentValueFormatted: string;
  totalSavings: number;
  totalSavingsFormatted: string;
}

/**
 * Format currency amount with cents when fractional, or whole units if .00.
 * Supports USD ($) and INR (₹).
 */
export function formatCurrency(amount: number, currency: string = 'USD'): string {
  const isINR = currency === 'INR';
  const symbol = isINR ? '₹' : '$';
  const locale = isINR ? 'en-IN' : 'en-US';

  if (Math.round(amount * 100) % 100 === 0) {
    return `${symbol}${Math.round(amount).toLocaleString(locale)}`;
  }
  return `${symbol}${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Helper function to calculate exact pricing, monthly rates, commitment totals, and savings.
 * Calculates exact fractional rates down to the cent without premature integer rounding.
 */
export function calculatePricing(
  baseMonthlyPrice: number,
  durationMonths: CommitmentDuration,
  commitmentOptions: CommitmentOption[] = COMMITMENT_OPTIONS,
  currency: string = 'USD'
): PricingCalculation {
  const option = commitmentOptions.find((opt) => opt.months === durationMonths) || {
    months: durationMonths,
    discountPercentage: 0,
    label: `${durationMonths} Months`
  };

  const discountPercentage = option.discountPercentage;
  // Calculate exact discounted monthly rate down to the cent (2 decimal places)
  const exactMonthlyRate = baseMonthlyPrice * (1 - discountPercentage / 100);
  const discountedMonthlyRate = Math.round(exactMonthlyRate * 100) / 100;
  
  // Total commitment value based on exact discounted rate * months
  const totalCommitmentValue = Math.round(discountedMonthlyRate * durationMonths * 100) / 100;
  const standardTotalValue = Math.round(baseMonthlyPrice * durationMonths * 100) / 100;
  const totalSavings = Math.round((standardTotalValue - totalCommitmentValue) * 100) / 100;

  return {
    baseMonthlyRate: baseMonthlyPrice,
    discountPercentage,
    discountedMonthlyRate,
    discountedMonthlyRateFormatted: formatCurrency(discountedMonthlyRate, currency),
    totalCommitmentValue,
    totalCommitmentValueFormatted: formatCurrency(totalCommitmentValue, currency),
    totalSavings,
    totalSavingsFormatted: formatCurrency(totalSavings, currency)
  };
}

/**
 * Approved EVERFORGE US Coaching Plans
 * Note: India pricing configuration remains unreleased/unpublished per directive until approved.
 */
export const COACHING_PLANS: CoachingPlan[] = [
  {
    id: 'group-coaching',
    name: 'Group Coaching',
    subtitle: 'Community-Driven Strength & Conditioning',
    tagline: 'Structured team programming with shared coaching accountability.',
    baseMonthlyPrice: 65,
    currency: 'USD',
    description: 'A progressive team-based training curriculum designed for steady strength progression, exercise mastery, and community accountability.',
    targetAudience: 'Individuals seeking structured programming and collective motivation at an accessible tier.',
    features: [
      'Progressive 4-week group strength & conditioning cycles',
      'Shared coaching community for questions & accountability',
      'Standard exercise form guidance and movement library',
      'General nutritional frameworks and protein guidance',
      'EVERFORGE Athlete Portal workout tracking'
    ],
    deliverables: {
      programming: 'Updated monthly team training block via portal',
      nutrition: 'Foundational macronutrient & protein guidance guide',
      checkIns: 'Monthly group check-in and Q&A session',
      videoAnalysis: 'Monthly form check breakdown in group board',
      messagingAccess: 'Community board and group coach interaction'
    }
  },
  {
    id: 'personalized-coaching',
    name: 'Personalized Coaching',
    subtitle: 'Customized Programming & Habit Oversight',
    tagline: 'Individualized workout programming and nutritional guidance tailored to your goals.',
    featured: true,
    baseMonthlyPrice: 129,
    currency: 'USD',
    description: 'Custom programming tailored to your schedule, training background, and equipment, paired with regular coach feedback.',
    targetAudience: 'Lifters wanting individualized programming without the requirement for weekly live calls.',
    features: [
      'Personalized periodized training blocks for your gym/home setup',
      'Customized macronutrient targets tailored to your metabolic goal',
      'Bi-weekly asynchronous form critique & technique refinement',
      'Bi-weekly progress check-ins with training adjustments',
      'In-app progress tracking and direct coach messaging'
    ],
    deliverables: {
      programming: 'Individualized 4-week autoregulated training cycles',
      nutrition: 'Calculated macro targets & weekly nutrition tracking',
      checkIns: 'Bi-weekly formal progress review and adjustments',
      videoAnalysis: 'Up to 3 lift analysis clips reviewed per week',
      messagingAccess: 'Direct coach chat (responses within 24-48 hours)'
    }
  },
  {
    id: 'one-on-one-coaching',
    name: '1:1 Online Coaching',
    subtitle: 'Comprehensive 1-on-1 Oversight & Video Coaching',
    tagline: 'Direct, dedicated partnership with close weekly coaching, calls, and continuous feedback.',
    baseMonthlyPrice: 229,
    currency: 'USD',
    description: 'Our premier individualized coaching relationship featuring weekly strategic reviews, high-frequency technique critique, and direct access.',
    targetAudience: 'Athletes and dedicated trainees committed to systematic progress, technical perfection, and direct coach mentorship.',
    features: [
      'Fully bespoke training programming updated dynamically',
      'Comprehensive nutrition protocols with regular calibration',
      'Weekly 1:1 strategy calls to review progress and metrics',
      'High-frequency video form reviews for all primary lifts',
      'Priority direct messaging for questions, adjustments, and feedback',
      'Travel and schedule adaptations on demand'
    ],
    deliverables: {
      programming: 'Weekly dynamic programming adaptations based on performance',
      nutrition: 'Individualized metabolic calibration & habit monitoring',
      checkIns: 'Weekly 1:1 strategy review call',
      videoAnalysis: 'Priority video critique on working sets',
      messagingAccess: 'Priority 1-on-1 coach chat with dedicated response window'
    }
  },
  {
    id: 'couple-coaching',
    name: 'Couple Coaching',
    subtitle: 'Dual Personalized Coaching for Partners',
    tagline: 'Shared commitment with independent, customized plans for couples training together.',
    baseMonthlyPrice: 299,
    currency: 'USD',
    description: 'Complete coaching for two partners. Each partner receives their own customized training and nutrition plan with joint accountability.',
    targetAudience: 'Partners, couples, or training pairs committed to reaching peak health and strength together.',
    features: [
      'Two distinct, fully customized training plans for each partner',
      'Individualized nutrition targets tailored to each person’s metabolism',
      'Joint check-ins and progress reviews for shared accountability',
      'Video technique reviews for both partners',
      'Direct coach messaging access for both athletes'
    ],
    deliverables: {
      programming: 'Dual personalized training tracks for both athletes',
      nutrition: 'Separate personalized nutrition targets and meal guidance',
      checkIns: 'Bi-weekly joint coaching review and progress sync',
      videoAnalysis: 'Form checks for both partners across working sets',
      messagingAccess: 'Joint and individual coach messaging access'
    }
  }
];

export const FAQS = [
  {
    question: 'How do I choose between Group, Personalized, 1:1 Online, and Couple Coaching?',
    answer: 'Group Coaching provides structured team programming with community support. Personalized Coaching offers an individualized training and nutrition plan with bi-weekly coach check-ins. 1:1 Online Coaching is our highest-touch service with weekly strategy calls, high-frequency form checks, and priority access. Couple Coaching provides separate customized plans for two partners at combined pricing.'
  },
  {
    question: 'How does form analysis and technique coaching work?',
    answer: 'You upload video clips of your working sets directly through the athlete portal. Your coach reviews your joint angles, bar path, tempo, and bracing, providing clear actionable feedback and cues for your subsequent sessions.'
  },
  {
    question: 'How do commitment durations and billing work?',
    answer: 'You can choose between 1-month, 3-month (10% discount), 6-month (15% discount), or 12-month (20% discount) commitment options. The discounted monthly rate and total savings are clearly calculated upfront with no hidden fees.'
  },
  {
    question: 'What happens after I submit a consultation request?',
    answer: 'Once you submit the intake inquiry, our coaching team reviews your fitness goals, experience level, and preferred service tier to ensure mutual fit before scheduling an introductory consultation.'
  }
];
